import { useEffect, useState } from 'react'
import { Plus, Trash2 } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type { OosBand, OosConfig, OosOperator, OosSeverity } from '@/types/cgt'
import {
  OOS_DEFAULT_COLORS,
  OOS_OPERATOR_LABELS,
  OOS_SEVERITIES,
  OOS_SEVERITY_LABELS,
  oosConfigProblem,
  oosFieldType,
  oosOperatorsFor,
} from '@/lib/oos'
import { UserGroupMultiSelect } from './UserGroupMultiSelect'

/**
 * A number input that keeps what the author is typing ("-", "1.") while
 * reporting only complete numbers, or undefined when cleared.
 */
function NumberInput({
  id,
  value,
  onChange,
  placeholder,
  ariaLabel,
}: {
  id?: string
  value: number | undefined
  onChange: (next: number | undefined) => void
  placeholder?: string
  ariaLabel: string
}) {
  const [text, setText] = useState(value === undefined ? '' : String(value))
  useEffect(() => {
    const parsed = text.trim() === '' ? undefined : Number(text)
    if (parsed !== value) setText(value === undefined ? '' : String(value))
    // Re-sync only when the value changes from outside this input; `text` is
    // deliberately not a dependency, or every keystroke would re-sync.
  }, [value])
  return (
    <Input
      id={id}
      inputMode='decimal'
      value={text}
      placeholder={placeholder}
      aria-label={ariaLabel}
      className='h-8 text-sm'
      onChange={e => {
        const next = e.target.value
        setText(next)
        if (next.trim() === '') onChange(undefined)
        else if (Number.isFinite(Number(next))) onChange(Number(next))
      }}
    />
  )
}

interface Props {
  spec: OosConfig | undefined
  onChange: (next: OosConfig | undefined) => void
  /** The field or cell type, in either vocabulary (select, dropdown, switch, checkbox, ...). */
  fieldType: string
  /** A dropdown's option labels. Empty when they are not known here. */
  options?: string[]
  /** Prefix for element ids, so two builders on one page do not share them. */
  idPrefix?: string
  /**
   * False for a calculated field: nobody types its value, so no save can be
   * asked for a comment and the switch is not offered.
   */
  allowComment?: boolean
}

/**
 * Specification for a field or table cell (z8nrz7eq4e, z8nrz7eq4h). A number
 * gets the in-spec range and the bands that grade a value outside it; the
 * other supported types get rules that grade the values they match. Both end
 * with the groups alerted to review a flagged value and the email switch.
 * Semantics are in lib/oos.ts.
 */
export function OosSpecBuilder({
  spec,
  onChange,
  fieldType,
  options = [],
  idPrefix = 'field-oos',
  allowComment = true,
}: Props) {
  const type = oosFieldType(fieldType)
  if (!type) return null
  const isNumber = type === 'number'

  if (!spec) {
    return (
      <div className='space-y-1.5'>
        <Label>Specification</Label>
        <Button
          variant='outline'
          size='sm'
          className='w-full'
          onClick={() =>
            onChange({
              bands: isNumber ? [] : [newRule(type)],
              alertGroupIds: [],
              emailGroups: true,
            })
          }
        >
          <Plus className='mr-1.5 h-3.5 w-3.5' />
          Add specification
        </Button>
        <p className='text-xs text-muted-foreground'>
          {isNumber
            ? 'Flag values outside an accepted range and alert a group to review them.'
            : 'Flag the values you choose and alert a group to review them.'}
        </p>
      </div>
    )
  }

  const setBand = (index: number, patch: Partial<OosBand>) =>
    onChange({ ...spec, bands: spec.bands.map((b, i) => (i === index ? { ...b, ...patch } : b)) })
  const removeBand = (index: number) =>
    onChange({ ...spec, bands: spec.bands.filter((_, i) => i !== index) })
  const addBand = () =>
    onChange({
      ...spec,
      bands: [...spec.bands, isNumber ? { severity: 'low', commentRequired: false } : newRule(type)],
    })
  const problem = oosConfigProblem(spec, type, type === 'dropdown' ? options : undefined)

  const severityRow = (band: OosBand, i: number, name: string) => (
    <>
      <div className='flex items-center gap-2'>
        <Select
          value={band.severity}
          onValueChange={v => setBand(i, { severity: v as OosSeverity, color: undefined })}
        >
          <SelectTrigger className='h-8 flex-1 text-sm' aria-label={`${name} ${i + 1} severity`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {OOS_SEVERITIES.map(s => (
              <SelectItem key={s} value={s}>
                {OOS_SEVERITY_LABELS[s]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <input
          type='color'
          aria-label={`${name} ${i + 1} colour`}
          className='h-8 w-10 shrink-0 cursor-pointer rounded border bg-background p-0.5'
          value={band.color ?? OOS_DEFAULT_COLORS[band.severity]}
          onChange={e => setBand(i, { color: e.target.value })}
        />
      </div>
      {allowComment && (
        <label className='flex items-center justify-between gap-2 text-xs'>
          <span>Comment required</span>
          <Switch
            checked={band.commentRequired}
            onCheckedChange={commentRequired => setBand(i, { commentRequired })}
          />
        </label>
      )}
    </>
  )

  return (
    <div className='space-y-3'>
      <div className='flex items-center justify-between'>
        <Label>Specification</Label>
        <button
          type='button'
          className='text-xs text-muted-foreground underline hover:text-foreground'
          onClick={() => onChange(undefined)}
        >
          Remove
        </button>
      </div>

      {isNumber ? (
        <>
          <div className='space-y-1.5'>
            <Label htmlFor={`${idPrefix}-low`} className='text-xs'>
              In spec
            </Label>
            <div className='flex items-center gap-2'>
              <NumberInput
                id={`${idPrefix}-low`}
                ariaLabel='Low limit'
                placeholder='Low'
                value={spec.low}
                onChange={low => onChange({ ...spec, low })}
              />
              <span className='text-xs text-muted-foreground'>to</span>
              <NumberInput
                ariaLabel='High limit'
                placeholder='High'
                value={spec.high}
                onChange={high => onChange({ ...spec, high })}
              />
            </div>
            <p className='text-[11px] text-muted-foreground'>
              Both limits count as in spec. Leave one empty for a one-sided limit.
            </p>
          </div>

          <div className='space-y-1.5'>
            <Label className='text-xs'>Out-of-spec bands</Label>
            {spec.bands.map((band, i) => (
              <div key={i} className='space-y-2 rounded-md border p-2'>
                <div className='flex items-center gap-2'>
                  <NumberInput
                    ariaLabel={`Band ${i + 1} from`}
                    placeholder='From'
                    value={band.min}
                    onChange={min => setBand(i, { min })}
                  />
                  <span className='shrink-0 text-xs text-muted-foreground'>to below</span>
                  <NumberInput
                    ariaLabel={`Band ${i + 1} to`}
                    placeholder='To'
                    value={band.max}
                    onChange={max => setBand(i, { max })}
                  />
                  <Button
                    variant='ghost'
                    size='icon'
                    className='h-8 w-8 shrink-0'
                    aria-label={`Remove band ${i + 1}`}
                    onClick={() => removeBand(i)}
                  >
                    <Trash2 className='h-3.5 w-3.5' />
                  </Button>
                </div>
                {severityRow(band, i, 'Band')}
              </div>
            ))}
            <Button variant='outline' size='sm' className='w-full' onClick={addBand}>
              <Plus className='mr-1.5 h-3.5 w-3.5' />
              Add band
            </Button>
            <p className='text-[11px] text-muted-foreground'>
              Bands are checked in order. A value outside the spec that no band covers is flagged High.
            </p>
          </div>
        </>
      ) : (
        <div className='space-y-1.5'>
          <Label className='text-xs'>Out-of-spec values</Label>
          {spec.bands.map((band, i) => {
            const when = band.when ?? { operator: oosOperatorsFor(type)[0], value: '' }
            const operators = oosOperatorsFor(type)
            return (
              <div key={i} className='space-y-2 rounded-md border p-2'>
                <div className='flex items-center gap-2'>
                  <span className='shrink-0 text-xs text-muted-foreground'>Value</span>
                  {operators.length > 1 ? (
                    <Select
                      value={when.operator}
                      onValueChange={v => setBand(i, { when: { ...when, operator: v as OosOperator } })}
                    >
                      <SelectTrigger className='h-8 w-auto min-w-24 text-sm' aria-label={`Rule ${i + 1} comparison`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {operators.map(op => (
                          <SelectItem key={op} value={op}>
                            {OOS_OPERATOR_LABELS[op]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <span className='shrink-0 text-xs text-muted-foreground'>{OOS_OPERATOR_LABELS[when.operator]}</span>
                  )}
                  <Button
                    variant='ghost'
                    size='icon'
                    className='ml-auto h-8 w-8 shrink-0'
                    aria-label={`Remove rule ${i + 1}`}
                    onClick={() => removeBand(i)}
                  >
                    <Trash2 className='h-3.5 w-3.5' />
                  </Button>
                </div>
                <RuleValueInput
                  type={type}
                  options={options}
                  ariaLabel={`Rule ${i + 1} value`}
                  value={when.value}
                  onChange={value => setBand(i, { when: { ...when, value } })}
                />
                {severityRow(band, i, 'Rule')}
              </div>
            )
          })}
          <Button variant='outline' size='sm' className='w-full' onClick={addBand}>
            <Plus className='mr-1.5 h-3.5 w-3.5' />
            Add value
          </Button>
          <p className='text-[11px] text-muted-foreground'>
            Rules are checked in order. The first one that matches grades the value. A value no rule matches is in spec.
          </p>
        </div>
      )}

      <UserGroupMultiSelect
        id={`${idPrefix}-alert-groups`}
        label='Alert groups'
        helpText='Members of these groups are alerted and review the value. The person who entered it cannot review it.'
        value={spec.alertGroupIds}
        onChange={alertGroupIds => onChange({ ...spec, alertGroupIds })}
      />

      <label className='flex items-center justify-between gap-2 text-sm'>
        <span>Email the groups</span>
        <Switch
          checked={spec.emailGroups === true}
          onCheckedChange={emailGroups => onChange({ ...spec, emailGroups })}
        />
      </label>

      {problem && (
        <p className='rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800'>
          {problem}
        </p>
      )}
    </div>
  )
}

/** A new rule for a non-number type: its first comparison, no value yet. */
function newRule(type: Exclude<ReturnType<typeof oosFieldType>, null>): OosBand {
  return {
    when: { operator: oosOperatorsFor(type)[0] ?? 'equals', value: '' },
    severity: 'low',
    commentRequired: false,
  }
}

/** The value a rule compares against, picked the way the type is filled. */
function RuleValueInput({
  type,
  options,
  ariaLabel,
  value,
  onChange,
}: {
  type: Exclude<ReturnType<typeof oosFieldType>, null>
  options: string[]
  ariaLabel: string
  value: string
  onChange: (next: string) => void
}) {
  const cls = 'h-8 text-sm'
  if (type === 'checkbox') {
    return (
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className={cls} aria-label={ariaLabel}>
          <SelectValue placeholder='Choose' />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value='true'>Checked</SelectItem>
          <SelectItem value='false'>Unchecked</SelectItem>
        </SelectContent>
      </Select>
    )
  }
  // Option labels when they are known here; an option set's labels may not
  // be, so free text is the fallback, as in the visibility builder.
  if (type === 'dropdown' && options.length > 0) {
    return (
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className={cls} aria-label={ariaLabel}>
          <SelectValue placeholder='Choose an option' />
        </SelectTrigger>
        <SelectContent>
          {options.filter(Boolean).map(o => (
            <SelectItem key={o} value={o}>
              {o}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    )
  }
  return (
    <Input
      type={type === 'date' ? 'date' : 'text'}
      className={cls}
      aria-label={ariaLabel}
      placeholder={type === 'date' ? undefined : 'Text'}
      value={value}
      onChange={e => onChange(e.target.value)}
    />
  )
}
